import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { listSnippets, useSnippet, type NoteSnippet } from '@/lib/api/snippets.js';

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
};

type PickerState = {
  open: true;
  trigger: string;
  triggerStart: number;
};

function groupSnippets(snippets: NoteSnippet[]) {
  const store: NoteSnippet[] = [];
  const personal: NoteSnippet[] = [];
  for (const s of snippets) {
    if (s.level === 'store') store.push(s);
    else personal.push(s);
  }
  return { store, personal };
}

export function SnippetTextarea({ value, onChange, className, ...rest }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [picker, setPicker] = useState<PickerState | null>(null);
  const [highlighted, setHighlighted] = useState(0);

  const snippetsQuery = useQuery({
    queryKey: ['snippets'],
    queryFn: () => listSnippets({ level: 'all' }),
    staleTime: 60_000,
  });

  const filtered = picker
    ? (snippetsQuery.data ?? []).filter((s) =>
        picker.trigger === ''
          ? true
          : s.triggerCode.startsWith(picker.trigger.toLowerCase()) ||
            s.title.toLowerCase().includes(picker.trigger.toLowerCase()),
      )
    : [];

  const { store, personal } = groupSnippets(filtered);
  const orderedFiltered = [...store, ...personal];

  useEffect(() => {
    setHighlighted(0);
  }, [picker?.trigger]);

  const detectTrigger = useCallback(
    (text: string, cursorPos: number) => {
      const before = text.slice(0, cursorPos);
      const match = before.match(/(?:^|[\s\n])(\/(\w*))$/);
      if (!match) {
        if (picker) setPicker(null);
        return;
      }
      const fullMatch = match[1]!;
      const trigger = match[2]!;
      const triggerStart = cursorPos - fullMatch.length;
      setPicker({ open: true, trigger, triggerStart });
    },
    [picker],
  );

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value;
    onChange(next);
    detectTrigger(next, e.target.selectionStart ?? next.length);
  }

  function insertSnippet(snippet: NoteSnippet) {
    if (!picker || !textareaRef.current) return;

    const before = value.slice(0, picker.triggerStart);
    const afterCursor = value.slice(
      picker.triggerStart + 1 + picker.trigger.length,
    );
    const next = before + snippet.content + afterCursor;
    onChange(next);
    setPicker(null);

    void useSnippet(snippet.id);

    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const pos = before.length + snippet.content.length;
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.focus();
    });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!picker || orderedFiltered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % orderedFiltered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h - 1 + orderedFiltered.length) % orderedFiltered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      const selected = orderedFiltered[highlighted];
      if (selected) {
        e.preventDefault();
        insertSnippet(selected);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setPicker(null);
    }
  }

  return (
    <div className="relative">
      <textarea
        {...rest}
        ref={textareaRef}
        value={value}
        className={[
          'min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary',
          className ?? '',
        ].join(' ')}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      {picker && (snippetsQuery.data?.length ?? 0) > 0 ? (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 z-50 mb-1 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          {store.length > 0 ? (
            <>
              <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-400 bg-slate-50">
                Store Snippets
              </div>
              {store.map((s, i) => {
                const idx = i;
                return (
                  <SnippetRow
                    key={s.id}
                    snippet={s}
                    active={orderedFiltered[highlighted]?.id === s.id}
                    onMouseEnter={() => setHighlighted(idx)}
                    onClick={() => insertSnippet(s)}
                  />
                );
              })}
            </>
          ) : null}
          {personal.length > 0 ? (
            <>
              <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-400 bg-slate-50">
                My Snippets
              </div>
              {personal.map((s, i) => {
                const idx = store.length + i;
                return (
                  <SnippetRow
                    key={s.id}
                    snippet={s}
                    active={orderedFiltered[highlighted]?.id === s.id}
                    onMouseEnter={() => setHighlighted(idx)}
                    onClick={() => insertSnippet(s)}
                  />
                );
              })}
            </>
          ) : null}
          {orderedFiltered.length === 0 && picker.trigger ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No snippets matching &quot;/{picker.trigger}&quot;
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SnippetRow({
  snippet,
  active,
  onMouseEnter,
  onClick,
}: {
  snippet: NoteSnippet;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[
        'flex w-full items-start gap-3 px-3 py-2 text-left text-sm',
        active ? 'bg-primary/10' : 'hover:bg-slate-50',
      ].join(' ')}
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
        /{snippet.triggerCode}
      </span>
      <span>
        <span className="block font-semibold text-slate-900">{snippet.title}</span>
        <span className="line-clamp-1 text-xs text-slate-500">{snippet.content}</span>
      </span>
    </button>
  );
}
